import { useMutation, useQuery } from "@apollo/client";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FETCH_LISTS as fetchLists } from "../../../../graphql/query/List";
import { CREATE_LIST as createListMutation } from "../../../../graphql/mutation/List";
import { LIST_TYPES } from "../../../../constants/listTypes";
import { artistFilterStore } from "../../components/useArtistFilters";
import searchedArtists from "./components/SearchedArtists";
import {
  Table,
  multiSelect,
  SelectList,
} from "../../../../components/Functional";
import Header from "../../../../components/ui/ListHeader";
import * as SelectListWorking from "./components/SelectListWorking";
import * as SelectListSaved from "./components/SelectListSaved";
import Actions from "./components/Actions";

const List = props => {
  const { formik } = props;
  const navigate = useNavigate();

  const { data: { lists: { edges = [] } = {} } = {}, loading: loadingWorkingList } = useQuery(fetchLists(), {
    variables: {
      input: { typeId: LIST_TYPES.ARTIST_WORKING },
    },
    fetchPolicy: "network-only",
  });

  const [createList] = useMutation(createListMutation(), {
    refetchQueries: [
      {
        query: fetchLists(),
        variables: {
          input: {
            typeId: LIST_TYPES.ARTIST_WORKING,
          },
        },
        fetchPolicy: "network-only",
      },
    ],
    awaitRefetchQueries: true,
  });

  // Get the first list's ID if it exists (even if empty, the list object will have an id)
  const workingListId = edges?.[0]?.id ?? null;

  // Ensure working list exists - create it if it doesn't
  useEffect(() => {
    if (!loadingWorkingList && !workingListId) {
      createList({
        variables: {
          input: {
            typeId: LIST_TYPES.ARTIST_WORKING,
            name: `select_list_working_${Date.now()}`,
          },
        },
      });
    }
  }, [loadingWorkingList, workingListId, createList]);

  const queryParams = new URLSearchParams(window.location.search);
  const status = queryParams?.get("status") || "all";
  const listIdParam = queryParams.get("listId");
  const listId = listIdParam ? +listIdParam : null;
  const ids = Object.keys(formik?.values?.selected?.ids || {})?.map(item =>
    parseInt(item.replace("_", "")),
  );

  useEffect(() => {
    const currentParams = new URLSearchParams(window.location.search);
    let shouldUpdate = false;
    
    // Remove workingListId if we're viewing a saved list (listId is present)
    if (listId && currentParams.has("workingListId")) {
      currentParams.delete("workingListId");
      shouldUpdate = true;
    }
    
    // Update workingListId if we're on selectListWorking tab, it's available, and no saved listId
    if (status === "selectListWorking" && workingListId && !listId) {
      if (currentParams.get("workingListId") !== String(workingListId)) {
        currentParams.set("workingListId", workingListId);
        shouldUpdate = true;
      }
    }
    
    if (shouldUpdate) {
      const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
      // eslint-disable-next-line
      history.pushState(null, null, newUrl);
    }
  }, [workingListId, status, listId]);

  const multiSelectProps = {
    formik,
    label: "Artists",
    name: "selected",
    keyName: "id",
  };

  const actionProps = {
    formik,
    ids,
    listId,
    workingListId,
    onSuccess: () => formik?.resetForm(),
    search: artistFilterStore.get(),
  };

  const handleChange = value => {
    formik?.resetForm();
    let url = `/artist?status=${value}`;
    
    // For working list tabs, use listId if present (saved list), otherwise use workingListId
    if (value === "selectListWorking" || value === "selectListSaved") {
      // Include listId if present (viewing/editing a saved list), otherwise use workingListId
      if (listId) {
        url += `&listId=${listId}`;
      } else if (workingListId) {
        url += `&workingListId=${workingListId}`;
      }
    } else {
      // For "all" and other tabs, preserve saved list IDs (listId) so items can be added to them
      // But don't include working list IDs
      if (listId) {
        url += `&listId=${listId}`;
      }
    }
    
    navigate(url);
  };

  const GeneralTabActions = () => {
    // Determine target list ID - use saved list ID if present, otherwise use working list ID
    const targetListId = listId && !isNaN(listId) ? listId : (workingListId || undefined);

    const createListProps = {
      ...actionProps,
      // Use listId if present (saved list), otherwise use workingListId
      ...(targetListId ? { targetListId } : {}),
      onSuccess: () => (
        formik?.resetForm(),
        navigate(
          `/artist?status=selectListWorking${listId ? `&listId=${listId}` : ""}`,
        )
      ),
      typeId: LIST_TYPES.ARTIST_WORKING,
    };

    return (
      <>
        <SelectList.CreateList {...createListProps} />
      </>
    );
  };

  const tabProps = {
    active: status,
    onChange: handleChange,
    tabs: [
      {
        actions: GeneralTabActions,
        label: "All Artists",
        name: "all",
        tableProps: searchedArtists(),
      },
      {
        actions: SelectListWorking.Actions,
        label: "Select List (Working)",
        name: "selectListWorking",
        tableProps: SelectListWorking.tableProps(actionProps),
      },
      {
        actions: SelectListSaved.Actions,
        label: "Select List (Saved)",
        name: "selectListSaved",
        tableProps: SelectListSaved.tableProps(actionProps),
      },
    ],
  };

  const { tableProps: tabTableProps, actions: tabActions } =
    tabProps?.tabs?.find(tab => tab.name === status) || {};

  const fixHeadersName = ({ status, headers }) =>
    headers?.map(header => {
      return {
        ...header,
        name:
          header?.name && status === "selectListWorking"
            ? Array.isArray(header?.name)
              ? header?.name?.map(item => `item.${item}`)
              : `item.${header?.name}`
            : header?.name,
      };
    });

  const tableProps = {
    headers: fixHeadersName({
      status,
      headers: [
        multiSelect(multiSelectProps),
        {
          label: "ID",
          name: "id",
          sortKey: "id",
          type: "label",
        },
        {
          label: "Name",
          name: "name",
          type: "label",
        },
        {
          label: "Country",
          name: "bornCountry.name",
          sortKey: "country",
          type: "label",
        },
        {
          label: "Represented",
          name: "represented",
          type: "custom",
          component: props => {
            let rep = "Represented";
            switch (props?.values?.represented) {
              case 0:
              default:
                rep = "Represented";
                break;
              case 1:
                rep = "External";
                break;
              case 2:
                rep = "Secondary Market";
                break;
              case 3:
                rep = "Not Represented";
                break;
            }
            return <div>{rep}</div>;
          },
        },
        {
          label: "Confidential",
          name: "confidential",
          sortKey: "confidential",
          type: "input",
          inputType: "toggle",
          designType: "threeStateCheckbox",
          disabled: true,
        },
        { label: "", type: "custom", component: Actions },
      ],
    }),
    tabProps,
    variant: "dynamicHeight",
    ...tabTableProps,
  };

  return (
    <>
        <Header
          leftText="All Artists"
          rightComponent={() => {
            return (
              <div className="flex gap-4">{tabActions?.(actionProps)}</div>
            );
          }}
        />
      <div className="mt-6 w-full">
        <Table {...tableProps} />
      </div>
    </>
  );
};

export default List;
